import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { Search } from 'lucide-react';
import ExportButton from '../../components/common/ExportButton';
import FilterSelect from '../../components/common/FilterSelect';
import { inventoryAPI } from '../../services/api';
import { useStoreContextStore } from '../../store/storeContextStore';
import { downloadExcelWorkbook } from '../../utils/exportExcel';

const formatLocalDateKey = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const DifferenceCell = ({ value }) => {
  const numericValue = Number(value || 0);
  const className = numericValue < 0
    ? 'text-red-600'
    : numericValue > 0
      ? 'text-orange-600'
      : 'text-green-600';

  return (
    <span className={`font-semibold ${className}`}>
      {numericValue}
    </span>
  );
};

const Inventory = () => {
  const selectedStore = useStoreContextStore((state) => state.selectedStore);
  const selectedStoreId = selectedStore?.storeInfoId || selectedStore?.store_info_id || selectedStore?.legacyStoreId || selectedStore?.legacy_store_id || '';
  const selectedStoreName = selectedStore?.name || selectedStore?.storeName || selectedStore?.storeCode || 'selected store';
  const todayKey = formatLocalDateKey();
  const [filter, setFilter] = useState({
    fromDate: todayKey,
    toDate: todayKey,
    search: '',
    differenceFilter: 'all',
  });

  const { data, isLoading } = useQuery(
    ['inventoryCountReport', filter, selectedStoreId],
    () => inventoryAPI.getCountReport({
      dateFrom: filter.fromDate,
      dateTo: filter.toDate,
      search: filter.search,
      differenceFilter: filter.differenceFilter,
      ...(selectedStoreId ? { storeId: selectedStoreId } : {}),
    }),
    {
      enabled: Boolean(selectedStoreId),
      refetchOnWindowFocus: false,
      refetchInterval: false,
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
    }
  );

  const rows = useMemo(() => data?.data?.data || [], [data]);
  const meta = data?.data?.meta || {};

  const subtitle = useMemo(() => {
    if (!selectedStoreId) return 'Select a store from the header to view inventory-count updates.';
    return `Showing latest inventory-count updates for ${selectedStoreName} from ${filter.fromDate} to ${filter.toDate}`;
  }, [filter.fromDate, filter.toDate, selectedStoreId, selectedStoreName]);

  const differenceOptions = useMemo(
    () => [
      { value: 'all', label: 'All Differences' },
      { value: 'below', label: 'Below System Stock Qty' },
      { value: 'exact', label: 'Exact Stock Qty' },
      { value: 'above', label: 'Above System Stock Qty' },
    ],
    []
  );

  const exportRows = () => {
    if (!rows.length) return;

    downloadExcelWorkbook({
      fileName: `daily-inventory-report-${meta.fromDate || filter.fromDate}-to-${meta.toDate || filter.toDate}.xlsx`,
      sheetName: 'Daily Inventory',
      columns: [
        { header: 'Store Name', key: 'storeName', width: 20 },
        { header: 'SKU', key: 'sku', width: 18 },
        { header: 'Product Name', key: 'productName', width: 34 },
        { header: 'Category Name', key: 'categoryName', width: 20 },
        { header: 'Parent Category Name', key: 'parentCategoryName', width: 24 },
        { header: 'Shop Count', width: 12, value: (row) => row.shopCount ?? row.shop_count ?? 0 },
        { header: 'Warehouse Count', width: 16, value: (row) => row.warehouseCount ?? row.warehouse_count ?? 0 },
        { header: 'Physical Stock Qty', width: 16, value: (row) => row.physicalStockQty ?? row.stockQtyPhysical ?? 0 },
        { header: 'System Stock Qty', width: 16, value: (row) => row.systemStockQty ?? 0 },
        { header: 'Difference', key: 'difference', width: 12 },
      ],
      rows,
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 border-b border-slate-200 pb-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-[24px] font-bold tracking-tight text-slate-900">Daily Inventory Report</h1>
          <p className="mt-1 text-sm text-slate-600">{subtitle}</p>
        </div>
        <ExportButton onClick={exportRows} disabled={!rows.length} />
      </div>

      <div className="card">
        <div className="flex flex-wrap gap-4">
          <div className="min-w-52">
            <label className="mb-1 block text-sm font-medium text-gray-700">From Date</label>
            <input
              type="date"
              value={filter.fromDate}
              max={filter.toDate}
              onChange={(event) => setFilter((current) => ({ ...current, fromDate: event.target.value }))}
              className="input"
            />
          </div>
          <div className="min-w-52">
            <label className="mb-1 block text-sm font-medium text-gray-700">To Date</label>
            <input
              type="date"
              value={filter.toDate}
              min={filter.fromDate}
              max={todayKey}
              onChange={(event) => setFilter((current) => ({ ...current, toDate: event.target.value }))}
              className="input"
            />
          </div>
          <div className="min-w-[280px] flex-1">
            <label className="mb-1 block text-sm font-medium text-gray-700">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 transform text-gray-400" />
              <input
                type="text"
                placeholder="Search by SKU, product, category, parent category, or store..."
                value={filter.search}
                onChange={(event) => setFilter((current) => ({ ...current, search: event.target.value }))}
                className="input pl-10"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="card overflow-hidden p-0">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Daily Inventory Count Results</h2>
            <p className="text-sm text-gray-500">
              Latest counted stock from the daily inventory task between {meta.fromDate || filter.fromDate} and {meta.toDate || filter.toDate}
            </p>
          </div>
          <div className="flex items-end gap-4">
            <FilterSelect
              label="Difference"
              value={filter.differenceFilter}
              options={differenceOptions}
              onChange={(nextValue) => setFilter((current) => ({ ...current, differenceFilter: nextValue }))}
              className="min-w-52"
            />
            <div className="pb-1 text-sm text-gray-500">
              {meta.total || 0} products
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Store Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">SKU</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Product Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Category Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Parent Category Name</th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Shop Count</th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Warehouse Count</th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Physical Stock Qty</th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">System Stock Qty</th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Difference</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {isLoading ? (
                <tr>
                  <td colSpan="10" className="px-6 py-8 text-center text-gray-500">
                    Loading inventory check results...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan="10" className="px-6 py-8 text-center text-gray-500">
                    No inventory-count products found for the selected date and filters.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.storeName}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-600">{row.sku}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{row.productName}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{row.categoryName}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{row.parentCategoryName}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-700">{row.shopCount ?? row.shop_count ?? 0}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-700">{row.warehouseCount ?? row.warehouse_count ?? 0}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">{row.physicalStockQty ?? row.physical_stock_qty ?? row.stockQtyPhysical ?? 0}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-700">{row.systemStockQty}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <DifferenceCell value={row.difference} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Inventory;
